// eslint-disable-next-line import/named
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { IServerSideGetRowsParams, SortModelItem } from 'ag-grid-enterprise'
import { allClaimRouteUrl, claimsTableInitialState } from 'features/claims/claimsModel'
import { ClaimsTypeEnum, IAllClaimRouteUrl, IClaim, IClaimsTableRoot, IUpdateTableRequest } from 'features/claims/types'
import { IEntityBag } from 'features/entities/types'
import { OBGridFilter } from 'types/tableTypes'

// eslint-disable-next-line import/named
interface IClaimsState {
  commonClaimsSorting: SortModelItem[]
  commonClaimsFilters: OBGridFilter
  claimsTable: IClaimsTableRoot
  claim: null | IClaim
  entityBag: null | IEntityBag
  allClaimRouteUrl: IAllClaimRouteUrl
  refreshClaimCount?: boolean | undefined
  downloadNotifier?: boolean
  triggerDownload?: boolean
  apiQuery?: string
  viewArchivedClaims?: boolean
}
export const claimsSliceInitialState: IClaimsState = {
  commonClaimsSorting: [],
  commonClaimsFilters: {},
  claim: null,
  entityBag: null,
  claimsTable: {
    ...claimsTableInitialState,
    apiQuery: '',
    statusFilter: claimsTableInitialState.statusFilter as ClaimsTypeEnum,
  },
  allClaimRouteUrl: allClaimRouteUrl,
  refreshClaimCount: false,
  downloadNotifier: false,
  triggerDownload: false,
  apiQuery: undefined,
  viewArchivedClaims: false,
}

export const claimsSlice = createSlice({
  name: 'claims',
  initialState: claimsSliceInitialState,
  reducers: {
    updateClaimCount: (state: IClaimsState, action: PayloadAction<{ count: number }>) => {
      const { count } = action.payload
      state.claimsTable.count = count
    },
    refreshClaimCount: (state: IClaimsState, action: PayloadAction<{ refreshCount?: boolean }>) => {
      const { refreshCount } = action.payload

      state.refreshClaimCount = refreshCount
    },
    updateSelectedRow: (state: IClaimsState, action: PayloadAction<{ selectedRow: any[] }>) => {
      const { selectedRow } = action.payload
      state.claimsTable.selectedRow = selectedRow
    },
    updateClaimsParams: (state: IClaimsState, action: PayloadAction<{ params: IServerSideGetRowsParams }>) => {
      state.claimsTable.params = action.payload.params
    },
    updateClaimsTable: (state: IClaimsState, action: PayloadAction<IUpdateTableRequest>) => {
      const { prop, value } = action.payload
      ;(state.claimsTable as any)[prop] = value

      if (['itemsPerPage', 'sorting'].includes(prop) && state.claimsTable.page !== 1) {
        state.claimsTable.page = 1
      }

      // Temp until we will have an user profile, sorting is always a string and we are saving it to the localStorage with the table name
      if (['sorting'].includes(prop)) {
        localStorage.setItem('claimsTableSorting', state.claimsTable[prop] as string)
      }
    },
    updateCommonClaimsFilters: (state: IClaimsState, action: PayloadAction<{ filters: OBGridFilter }>) => {
      const { filters } = action.payload
      state.commonClaimsFilters = filters

      if (state.claimsTable.page !== 1) state.claimsTable.page = 1
    },
    updateClaimsApiQuery: (state: IClaimsState, action: PayloadAction<{ query: string }>) => {
      const { query } = action.payload
      state.apiQuery = query
    },
    resetCommonClaimsFilters: (state: IClaimsState) => {
      state.commonClaimsFilters = {}

      if (state.claimsTable.page !== 1) state.claimsTable.page = 1
      // Dispatches a new API request
      // state.claimsTable.apiQuery = createApiQueryFromState(state.claimsTable)
    },
    reset(state: IClaimsState) {
      Object.assign(state, claimsSliceInitialState)
    },
    updateAllClaimsRoute: (state: IClaimsState, action: PayloadAction<IAllClaimRouteUrl>) => {
      state.allClaimRouteUrl = action.payload
    },
    updateDownloadNotifier: (state: IClaimsState, action: PayloadAction<boolean>) => {
      state.downloadNotifier = action.payload
    },
    updateTriggerDownload: (state: IClaimsState, action: PayloadAction<boolean>) => {
      state.triggerDownload = action.payload
    },
    toggleArchivedView: (state: IClaimsState, action: PayloadAction<boolean>) => {
      state.viewArchivedClaims = action.payload
      // Reset page to 1 when toggling view
      if (state.claimsTable.page !== 1) state.claimsTable.page = 1
    },
  },
})

export const {
  updateClaimsTable,
  reset,
  updateClaimCount,
  refreshClaimCount,
  updateClaimsApiQuery,
  updateClaimsParams,
  updateAllClaimsRoute,
  updateCommonClaimsFilters,
  resetCommonClaimsFilters,
  updateDownloadNotifier,
  updateTriggerDownload,
  updateSelectedRow,
  toggleArchivedView,
} = claimsSlice.actions

export const claimsReducer = claimsSlice.reducer
